import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const WORLD_TOPO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/**
 * World map with a red dot per approved member. Pins are clickable — they
 * navigate to the member's profile. The user can zoom in/out to inspect
 * dense clusters.
 */
const MembersMap = ({ members = [], canSeeCount = false, accessStatus = "none", onRequestAccess }) => {
  const navigate = useNavigate();
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });

  const dots = members.filter(
    (m) =>
      typeof m.lat === "number" &&
      typeof m.lng === "number" &&
      !Number.isNaN(m.lat) &&
      !Number.isNaN(m.lng)
  );

  const zoom = (factor) => {
    setPosition((p) => {
      const next = Math.max(1, Math.min(8, p.zoom * factor));
      return { ...p, zoom: next };
    });
  };
  const reset = () => setPosition({ coordinates: [0, 20], zoom: 1 });

  return (
    <div className="members-map" data-testid="members-map">
      <div className="members-map-inner">
        <div className="map-zoom-controls" role="group" aria-label="Zoom controls">
          <button type="button" onClick={() => zoom(1.5)} aria-label="Zoom in" data-testid="members-map-zoom-in"><ZoomIn size={14} /></button>
          <button type="button" onClick={() => zoom(1 / 1.5)} aria-label="Zoom out" data-testid="members-map-zoom-out"><ZoomOut size={14} /></button>
          <button type="button" onClick={reset} aria-label="Reset view" data-testid="members-map-reset"><Maximize2 size={14} /></button>
        </div>
        <ComposableMap
          projection="geoEqualEarth"
          width={980}
          height={470}
          projectionConfig={{ scale: 175 }}
        >
          <ZoomableGroup
            zoom={position.zoom}
            center={position.coordinates}
            minZoom={1}
            maxZoom={8}
            onMoveEnd={setPosition}
          >
            <Geographies geography={WORLD_TOPO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#111"
                    stroke="rgba(255,255,255,0.14)"
                    strokeWidth={0.6}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>
            {dots.map((m) => (
              <Marker
                key={m.id}
                coordinates={[m.lng, m.lat]}
                onClick={() => navigate(`/members/${m.id}`)}
                data-testid={`map-dot-${m.id}`}
                style={{
                  default: { cursor: "pointer" },
                  hover: { cursor: "pointer" },
                  pressed: { cursor: "pointer" },
                }}
              >
                <title>
                  {m.name}
                  {m.location_display ? ` · ${m.location_display}` : ""}
                </title>
                <circle r={5 / Math.sqrt(position.zoom)} className="members-map-dot" />
              </Marker>
            ))}
          </ZoomableGroup>
        </ComposableMap>
      </div>
      <div className="members-map-caption">
        <span className="members-map-legend"><span className="members-map-legend-dot" /> approved member</span>
        {canSeeCount ? (
          <span className="members-map-count" data-testid="members-map-count">
            {dots.length} of {members.length} member{members.length === 1 ? "" : "s"} on the map
          </span>
        ) : (
          <span className="members-map-count-hidden" data-testid="members-count-hidden">
            {accessStatus === "pending" ? (
              <>Access requested — awaiting approval</>
            ) : accessStatus === "denied" ? (
              <>Access to the count was denied</>
            ) : onRequestAccess ? (
              <button
                type="button"
                className="link-btn"
                onClick={onRequestAccess}
                data-testid="members-request-count-access"
              >
                Request to see the count →
              </button>
            ) : (
              <>Sign in to request the count</>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default MembersMap;
